import type { QueryClient } from "@tanstack/react-query";
import { ChatKeys } from "web_myplann/Api/Chat/ChatKeys";
import type { ChatMessageView } from "web_myplann/Chat/Types/chatMessage";
import type { ChatMessagesCache } from "web_myplann/Chat/Types/chatMessagesPage";

const mapPages = (
	cache: ChatMessagesCache,
	mapper: (items: ChatMessageView[], pageIndex: number) => ChatMessageView[],
): ChatMessagesCache => ({
	...cache,
	pages: cache.pages.map((page, pageIndex) => ({
		...page,
		items: mapper(page.items, pageIndex),
	})),
});

export const findNewestPageIndex = (cache: ChatMessagesCache) => {
	let newestPageIndex = -1;
	let newestTime = Number.NEGATIVE_INFINITY;

	cache.pages.forEach((page, pageIndex) => {
		for (const item of page.items) {
			const time = item.createdAt.getTime();
			if (Number.isNaN(time)) continue;
			if (time > newestTime) {
				newestTime = time;
				newestPageIndex = pageIndex;
			}
		}
	});

	if (newestPageIndex !== -1) return newestPageIndex;

	return cache.pages.length > 0 ? 0 : -1;
};

const withLocalAttachmentUrls = (
	message: ChatMessageView,
	optimistic: ChatMessageView,
): ChatMessageView => ({
	...message,
	attachments: message.attachments.map((attachment, index) => ({
		...attachment,
		localUrl: optimistic.attachments[index]?.localUrl,
	})),
});

const updateCache = (
	queryClient: QueryClient,
	conversationId: number | null,
	updater: (cache: ChatMessagesCache) => ChatMessagesCache,
) => {
	const queryKey = ChatKeys.messages(conversationId);
	const cache = queryClient.getQueryData<ChatMessagesCache>(queryKey);
	if (!cache) return false;

	queryClient.setQueryData<ChatMessagesCache>(queryKey, updater(cache));
	return true;
};

export const appendOptimisticChatMessage = (
	queryClient: QueryClient,
	conversationId: number | null,
	message: ChatMessageView,
) =>
	updateCache(queryClient, conversationId, (cache) => {
		const targetPageIndex = findNewestPageIndex(cache);
		if (targetPageIndex === -1) return cache;

		return mapPages(cache, (items, pageIndex) =>
			pageIndex === targetPageIndex ? [...items, message] : items,
		);
	});

export const replaceOptimisticChatMessage = (
	queryClient: QueryClient,
	conversationId: number | null,
	clientId: string,
	message: ChatMessageView,
) => {
	let replaced = false;

	updateCache(queryClient, conversationId, (cache) =>
		mapPages(cache, (items) =>
			items.map((item) => {
				if (item.clientId !== clientId) return item;
				replaced = true;
				return {
					...withLocalAttachmentUrls(message, item),
					clientId,
					createdAt: item.createdAt,
					sequence: item.sequence,
				};
			}),
		),
	);

	return replaced;
};

export const appendConfirmedChatMessage = (
	queryClient: QueryClient,
	conversationId: number | null,
	message: ChatMessageView,
) =>
	updateCache(queryClient, conversationId, (cache) => {
		const exists = cache.pages.some((page) =>
			page.items.some(
				(item) => item.status === undefined && item.id === message.id,
			),
		);
		if (exists) return cache;

		const targetPageIndex = findNewestPageIndex(cache);
		if (targetPageIndex === -1) return cache;

		return mapPages(cache, (items, pageIndex) =>
			pageIndex === targetPageIndex ? [...items, message] : items,
		);
	});

export const removeOptimisticChatMessage = (
	queryClient: QueryClient,
	conversationId: number | null,
	clientId: string,
) =>
	updateCache(queryClient, conversationId, (cache) =>
		mapPages(cache, (items) =>
			items.filter((item) => item.clientId !== clientId),
		),
	);

export const markChatMessageFailed = (
	queryClient: QueryClient,
	conversationId: number | null,
	clientId: string,
) =>
	updateCache(queryClient, conversationId, (cache) =>
		mapPages(cache, (items) =>
			items.map((item) =>
				item.clientId === clientId ? { ...item, status: "failed" } : item,
			),
		),
	);

export const dropOptimisticChatMessages = (
	queryClient: QueryClient,
	conversationId: number | null,
	keptClientIds: ReadonlySet<string> = new Set(),
) =>
	updateCache(queryClient, conversationId, (cache) =>
		mapPages(cache, (items) =>
			items.filter(
				(item) =>
					item.status !== "pending" ||
					(item.clientId !== undefined && keptClientIds.has(item.clientId)),
			),
		),
	);

export const seedChatMessagesCache = (
	queryClient: QueryClient,
	conversationId: number | null,
	message: ChatMessageView,
) => {
	queryClient.setQueryData<ChatMessagesCache>(
		ChatKeys.messages(conversationId),
		{
			pages: [{ items: [message], nextCursor: null }],
			pageParams: [null],
		},
	);
};
